import { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { useToast } from '../../../contexts/ToastContext';
import {
  obtenerCatalogos, crearTipo, crearPlantilla, habilitarPlantilla,
  type Catalogos,
} from '../../../services/correoService';
import { mensajeError } from './errores';
import styles from './CorreoModule.module.css';

/** RF-04 — Catálogos: tipos de correo y plantillas. */
export default function CatalogosCorreoPage() {
  const { addToast } = useToast();
  const [cat, setCat] = useState<Catalogos | null>(null);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  // Formularios de alta.
  const [tipo, setTipo] = useState({ codigo: '', descripcion: '', esAutenticacion: false });
  const [plantilla, setPlantilla] = useState({ codigo: '', asunto: '', cuerpoHtml: '', variables: '', habilitar: true });

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      setCat(await obtenerCatalogos());
    } catch (err) {
      addToast('error', mensajeError(err));
    } finally {
      setCargando(false);
    }
  }, [addToast]);

  useEffect(() => { cargar(); }, [cargar]);

  const guardarTipo = async () => {
    setGuardando(true);
    try {
      await crearTipo({ codigo: tipo.codigo, descripcion: tipo.descripcion, esAutenticacion: tipo.esAutenticacion });
      addToast('success', `Tipo ${tipo.codigo.toUpperCase()} creado.`);
      setTipo({ codigo: '', descripcion: '', esAutenticacion: false });
      await cargar();
    } catch (err) {
      addToast('error', mensajeError(err));
    } finally {
      setGuardando(false);
    }
  };

  const guardarPlantilla = async () => {
    setGuardando(true);
    try {
      const variables = plantilla.variables.split(',').map((v) => v.trim()).filter(Boolean);
      await crearPlantilla({
        codigo: plantilla.codigo, asunto: plantilla.asunto, cuerpoHtml: plantilla.cuerpoHtml,
        variables, habilitar: plantilla.habilitar,
      });
      addToast('success', `Plantilla ${plantilla.codigo.toUpperCase()} creada.`);
      setPlantilla({ codigo: '', asunto: '', cuerpoHtml: '', variables: '', habilitar: true });
      await cargar();
    } catch (err) {
      addToast('error', mensajeError(err));
    } finally {
      setGuardando(false);
    }
  };

  const alternar = async (id: number, habilitar: boolean) => {
    try {
      await habilitarPlantilla({ id, habilitar });
      await cargar();
    } catch (err) {
      addToast('error', mensajeError(err));
    }
  };

  if (cargando) return <div className={styles.vacio}><Loader2 className="spinner" size={28} /></div>;

  return (
    <div>
      {/* ── Tipos de correo ── */}
      <section className={styles.seccion}>
        <div className={styles.seccionTitulo}>Tipos de correo</div>
        <div className={styles.form}>
          <div className={styles.campo}>
            <label htmlFor="t-codigo">Código</label>
            <input id="t-codigo" value={tipo.codigo} placeholder="PIN_ACCESO"
              onChange={(e) => setTipo((t) => ({ ...t, codigo: e.target.value }))} />
          </div>
          <div className={styles.campo}>
            <label htmlFor="t-desc">Descripción</label>
            <input id="t-desc" value={tipo.descripcion}
              onChange={(e) => setTipo((t) => ({ ...t, descripcion: e.target.value }))} />
          </div>
          <div className={styles.campo}>
            <label htmlFor="t-auth">
              <input id="t-auth" type="checkbox" checked={tipo.esAutenticacion}
                onChange={(e) => setTipo((t) => ({ ...t, esAutenticacion: e.target.checked }))} /> Es de autenticación
            </label>
          </div>
          <Button size="sm" loading={guardando} onClick={guardarTipo}
            disabled={!tipo.codigo || !tipo.descripcion}>
            <Plus size={15} /> Agregar tipo
          </Button>
        </div>

        <div className={styles.tablaScroll}>
          <table className={styles.tabla}>
            <thead><tr><th>Código</th><th>Descripción</th><th>Autenticación</th></tr></thead>
            <tbody>
              {(cat?.tipos ?? []).map((t) => (
                <tr key={t.tipoCorreoId}>
                  <td><span className={styles.chip}>{t.codigo}</span></td>
                  <td>{t.descripcion}</td>
                  <td>{t.esAutenticacion ? 'Sí' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Plantillas ── */}
      <section className={styles.seccion}>
        <div className={styles.seccionTitulo}>Plantillas</div>
        <div className={styles.form}>
          <div className={styles.campo}>
            <label htmlFor="p-codigo">Código</label>
            <input id="p-codigo" value={plantilla.codigo} placeholder="RECORDATORIO_CITA"
              onChange={(e) => setPlantilla((p) => ({ ...p, codigo: e.target.value }))} />
          </div>
          <div className={styles.campo}>
            <label htmlFor="p-asunto">Asunto</label>
            <input id="p-asunto" value={plantilla.asunto}
              onChange={(e) => setPlantilla((p) => ({ ...p, asunto: e.target.value }))} />
          </div>
          <div className={styles.campo}>
            <label htmlFor="p-vars">Variables (separadas por coma)</label>
            <input id="p-vars" value={plantilla.variables} placeholder="nombre, fecha, lugar"
              onChange={(e) => setPlantilla((p) => ({ ...p, variables: e.target.value }))} />
          </div>
          <div className={`${styles.campo} ${styles.formFull}`}>
            <label htmlFor="p-html">Cuerpo HTML</label>
            <textarea id="p-html" rows={4} value={plantilla.cuerpoHtml}
              style={{ padding: '0.45rem 0.6rem', border: '1px solid var(--color-border)', borderRadius: 6, fontFamily: 'var(--font-body)' }}
              onChange={(e) => setPlantilla((p) => ({ ...p, cuerpoHtml: e.target.value }))} />
          </div>
          <div className={styles.campo}>
            <label htmlFor="p-hab">
              <input id="p-hab" type="checkbox" checked={plantilla.habilitar}
                onChange={(e) => setPlantilla((p) => ({ ...p, habilitar: e.target.checked }))} /> Habilitar al crear
            </label>
          </div>
          <Button size="sm" loading={guardando} onClick={guardarPlantilla}
            disabled={!plantilla.codigo || !plantilla.asunto || !plantilla.cuerpoHtml}>
            <Plus size={15} /> Nueva versión
          </Button>
        </div>

        <div className={styles.tablaScroll}>
          <table className={styles.tabla}>
            <thead><tr><th>Código</th><th>Versión</th><th>Asunto</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {(cat?.plantillas ?? []).map((p) => (
                <tr key={p.plantillaId}>
                  <td><span className={styles.chip}>{p.codigo}</span></td>
                  <td>v{p.version}</td>
                  <td>{p.asunto}</td>
                  <td>
                    <span className={`${styles.chip} ${p.habilitada ? styles.chipOk : styles.chipWarn}`}>
                      {p.habilitada ? 'Habilitada' : 'Deshabilitada'}
                    </span>
                  </td>
                  <td>
                    <Button variant="ghost" size="sm" onClick={() => alternar(p.plantillaId, !p.habilitada)}>
                      {p.habilitada ? 'Deshabilitar' : 'Habilitar'}
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
